import { useState } from "react";

import { useScrollLock } from "../../hooks/useScrollLock";

import {
  ModalContainer,
  SelectedPlanInfo,
  PaymentOptions,
  PaymentQuestion,
  PaymentAnswer,
  Row,
  ModalLink,
  PaymentDescription,
  Text,
} from "./PaymentModal.styles";

import { ReactComponent as ChevronSvg } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { ReactComponent as BtcSvg } from "../../assets/icons/btc.svg";
import { ReactComponent as TelegramSvg } from "../../assets/icons/telegramBlue.svg";
import Button from "../utils/Button/Button";
import Modal from "../utils/Modal/Modal";
import { useNavigate } from "react-router-dom";

const PaymentModal = ({
  isOpen,
  onClose,
  selectedPlan,
  billingPeriod,
  handleCardPayment,
}) => {
  const navigate = useNavigate();
  const [expandedOption, setExpandedOption] = useState("card");

  useScrollLock(isOpen);

  if (!isOpen || !selectedPlan) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const toggleOption = (option) => {
    setExpandedOption(option);
  };

  const actionCardPayment = (type) => {
    handleCardPayment(type);
  };

  const handleEmailClick = () => {
    const recipient = "support@playbox.vip";

    const subject = "Support Request - Playbox.vip";
    const body = `Hello Support Team,

I am experiencing the following issue:

[Please describe your problem here]

Thank you,
[Your Name]`;

    // Encode subject and body for the URL
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);

    const mailtoLink = `mailto:${recipient}?subject=${encodedSubject}&body=${encodedBody}`;
    window.location.href = mailtoLink;
  };

  return (
    <Modal onClose={handleOverlayClick} isOpen={isOpen}>
      <ModalContainer $bigOpen={expandedOption === "card"}>
        <Row>
          <SelectedPlanInfo $gradient={selectedPlan.gradient}>
            {selectedPlan.name} Plan (
            {billingPeriod === "yearly" ? "Yearly" : "Monthly"})
          </SelectedPlanInfo>

          <CloseSvg onClick={onClose} style={{ cursor: "pointer" }} />
        </Row>

        <PaymentOptions>
          <PaymentQuestion onClick={() => toggleOption("card")}>
            <span>Credit / Debit Card</span>

            <ChevronSvg
              style={{
                transition: "transform 0.3s ease-in-out",
                transform:
                  expandedOption !== "card"
                    ? "rotate(-90deg)"
                    : "rotate(90deg)",
              }}
            />
          </PaymentQuestion>

          {expandedOption === "card" && (
            <PaymentAnswer $bigOpen $show={expandedOption === "card"}>
              <PaymentDescription>
                Purchasing our subscription through Telegram is a simple
                two-step process: 1. You purchase Stars (via the official{" "}
                <Text
                  $link
                  onClick={() =>
                    window.open("https://t.me/PremiumBot", "_blank")
                  }
                >
                  @PremiumBot
                </Text>
                ) from Telegram using your card (or Google Pay, Apple Pay, or
                other methods). 2. You use the Stars you purchased to buy our
                service (by paying the Stars to our bot).
              </PaymentDescription>

              <PaymentDescription>
                For detailed step-by-step guidance, please refer to our Telegram
                Payment{" "}
                <ModalLink
                  onClick={() => {
                    navigate("/telegram-tutorial");
                    setTimeout(() => {
                      window.scrollTo(0, 0);
                    }, 300);
                  }}
                >
                  Tutorial
                </ModalLink>
                .
              </PaymentDescription>

              <PaymentDescription>
                We adhere to the principle of transparent pricing, and due to
                high channel fees, the amount you pay may be higher than the
                price we offer.
              </PaymentDescription>

              <PaymentDescription>
                This payment method does NOT automatically renew.
              </PaymentDescription>
              <PaymentDescription>
                If you don't see the credits, please contact us via{" "}
                <ModalLink onClick={handleEmailClick}>email</ModalLink>.
              </PaymentDescription>

              <Button
                onClick={() => {
                  actionCardPayment("TELEGRAM");
                }}
                title={"Card with Telegram"}
                rightIcon={<TelegramSvg />}
                style={{ width: "100%" }}
              />
            </PaymentAnswer>
          )}
        </PaymentOptions>

        <PaymentOptions>
          <PaymentQuestion onClick={() => toggleOption("cryptocurrency")}>
            <span>Cryptocurrency</span>

            <ChevronSvg
              style={{
                transition: "transform 0.3s ease-in-out",
                transform:
                  expandedOption !== "cryptocurrency"
                    ? "rotate(-90deg)"
                    : "rotate(90deg)",
              }}
            />
          </PaymentQuestion>

          {expandedOption === "cryptocurrency" && (
            <PaymentAnswer
              $bigOpen={false}
              $show={expandedOption === "cryptocurrency"}
            >
              <PaymentDescription>
                Please make sure you have paid the full amount of currency
                (after deducting network fees) and confirmed that you selected
                the correct coin and network when making a payment to avoid your
                payment being held.
              </PaymentDescription>

              <PaymentDescription>
                For detailed step-by-step guidance, please refer to our Crypto
                Payment{" "}
                <ModalLink
                  onClick={() => {
                    navigate("/crypto-tutorial");
                    setTimeout(() => {
                      window.scrollTo(0, 0);
                    }, 300);
                  }}
                >
                  Tutorial
                </ModalLink>
                .
              </PaymentDescription>

              <PaymentDescription>
                This payment method does NOT automatically renew.
              </PaymentDescription>

              <PaymentDescription>
                If you don't see the credits, please contact us via{" "}
                <ModalLink onClick={handleEmailClick}>email</ModalLink>.
              </PaymentDescription>

              <Button
                onClick={() => {
                  actionCardPayment("CRYPTO");
                }}
                title={"Pay with Crypto"}
                rightIcon={<BtcSvg />}
                style={{ width: "100%" }}
              />
            </PaymentAnswer>
          )}
        </PaymentOptions>
      </ModalContainer>
    </Modal>
  );
};

export default PaymentModal;
